import { Component, Input, SimpleChanges, Type } from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { Subject, takeUntil } from 'rxjs';

// Import lib
import { encryptObject } from 'src/app/lib/cryptoJs';
import isEmpty from 'src/app/lib/isEmpty';
import { convert, toFixed, toFixedDown, truncateDecimals } from 'src/app/lib/roundOf';
import { ShareService } from 'src/app/services/share.service';

// Import service
import { SpotService } from 'src/app/services/spot.service';
import { AuthFacade } from 'src/app/store/auth';
import { MarketPriceFacade } from 'src/app/store/marketprice';

// Import facade
import { TradeAssetFacade } from 'src/app/store/trade-asset';
import { TradePairFacade } from 'src/app/store/tradepair';
import { UserFacade } from 'src/app/store/user';

// Import directives

@Component({
  selector: 'app-limitorder-form',
  templateUrl: './limitorder-form.component.html',
  styleUrls: ['./limitorder-form.component.css'],
})
export class LimitorderFormComponent {
  @Input() buyorsell: any;
  @Input() updateBuyForm: any;
  @Input() updateSellForm: any;

  closed$ = new Subject<any>();

  truncateDecimals = truncateDecimals;
  toFixedDown = toFixedDown;
  toFixed = toFixed;

  marketPrice: any = {}
  firstCurrency: any = {}
  secondCurrency: any = {}
  tradePair: any = {}
  auth: any = {}

  form: FormGroup;
  buyPercentage: number = 0
  submitted: boolean = false;
  serverErrors: any = {}
  tdsapply: boolean = false;

  cooldown: number = 0;
  timerInterval: any = null;

  constructor(
    private _fb: FormBuilder,
    private tradeAssetFacade: TradeAssetFacade,
    private tradePairFacade: TradePairFacade,
    private service: SpotService,
    private toastr: ToastrService,
    private authFacade: AuthFacade,
    private router: Router,
    private share: ShareService,
    private marketPriceFacade: MarketPriceFacade,
  ) {
    this.form = this._fb.group({
      price: ['', Validators.required],
      quantity: ['', Validators.required],
      total: ['']
    });
    this.authFacade.auth$.subscribe({
      next: (userAuth: any) => {
        this.auth = userAuth
      },
      error: (err: any) => { }
    })
  }

  ngOnInit() {

   

    this.tradeAssetFacade.tradeAsset$.subscribe({
      next: ({ firstCurrency, secondCurrency }: any) => {
        this.firstCurrency = firstCurrency
        this.secondCurrency = secondCurrency
      },
      error: (err: any) => { }
    })
    this.tradePairFacade.tradePair$.subscribe({
      next: (pair: any) => {
        this.tradePair = pair
      },
      error: (err: any) => { }
    })
    this.marketPriceFacade.marketPrice$.subscribe({
      next: (price: any) => {
        this.marketPrice = price
      },
      error: (err: any) => { }
    })
  }

  ngOnChanges(changes: SimpleChanges) {
     if (changes['buyorsell'] && changes['buyorsell'].currentValue) {
      this.restoreCooldown(); 
    }
    if (changes['updateBuyForm'] && !changes['updateBuyForm'].firstChange) {
      const values = changes['updateBuyForm'].currentValue
      this.form.patchValue({
        price: convert(values.price),
        quantity: convert(values.quantity),
        total: convert(values.total),
      });
    }

    if (changes['updateSellForm'] && !changes['updateSellForm'].firstChange) {
      const values = changes['updateSellForm'].currentValue
      this.form.patchValue({
        price: convert(values.price),
        quantity: convert(values.quantity),
        total: convert(values.total),
      });
    }
  }

  ngOnDestroy() {
    // this.closed$.next(); // <-- close open subscriptions
  }

  get price() { return this.form.get('price') }
  get quantity() { return this.form.get('quantity') }

  handleChange() {
    this.serverErrors = {}
    let { price, quantity } = this.form.value
    price = toFixedDown(price, this.tradePair.secondFloatDigit)
    quantity = toFixedDown(quantity, this.tradePair.firstFloatDigit)

    const total = price * quantity
    this.form.patchValue({
      total: truncateDecimals(total, this.tradePair.secondFloatDigit)
    })
  }

  handleChangeTotal() {
    let { price, total } = this.form.value
    let newQty: number | "" = ""

    if (!/^\d*\.?\d*$/.test(total)) {
      return;
    }

    if (!isEmpty(price) && !isEmpty(total)) {
      newQty = truncateDecimals(total * price, this.tradePair.firstFloatDigit)
    }
    
    this.form.patchValue({
      quantity: newQty
    })
  }

  handleSetPercentage(percent: number) {
    this.buyPercentage = percent
    const price = isEmpty(this.form.value.price) ? this.marketPrice.markPrice : this.form.value.price

    const availBalance = this.buyorsell == 'buy' ? this.secondCurrency.spotBal : this.firstCurrency.spotBal
    const selectedBalance = (availBalance / 100) * percent

    const quantity = selectedBalance / price

    if (this.buyorsell === 'buy') {
      this.form.patchValue({
        price: truncateDecimals(price, this.tradePair.secondFloatDigit),
        quantity: truncateDecimals(quantity, this.tradePair.firstFloatDigit),
        total: truncateDecimals(selectedBalance, this.tradePair.secondFloatDigit)
      })
    } else if (this.buyorsell === 'sell') {
      const total = selectedBalance * price
      this.form.patchValue({
        price: truncateDecimals(price, this.tradePair.secondFloatDigit),
        quantity: truncateDecimals(selectedBalance, this.tradePair.firstFloatDigit),
        total: truncateDecimals(total, this.tradePair.secondFloatDigit)
      })
    }
  }

  IncDec(field: string, type: string) {
    let value = this.form.value[field];
    if (type === 'dec' && value > 0)
      value--;
    else if (type === 'inc')
      value++;

    this.form.patchValue({
      [field]: value
    });
    this.handleChange()
  }

  submitForm() {
    let { price, quantity } = this.form.value
    price = toFixedDown(price, this.tradePair.secondFloatDigit)
    quantity = toFixedDown(quantity, this.tradePair.firstFloatDigit)

    this.submitted = true

    if (!this.form.valid || this.cooldown > 0) {
      this.submitted = false;
      return;
    }

      const expiryTime = Date.now() + 60000; 
      this.cooldown = 60;
      localStorage.setItem(`limitOrderCooldown_${this.buyorsell}`, expiryTime.toString());
      this.startCooldownTimer();
      

    let reqData = {
      price,
      quantity,
      buyorsell: this.buyorsell,
      orderType: 'limit',
      spotPairId: this.tradePair._id,
      newdate: new Date()
    }
    const encryptData = {
      token: encryptObject(reqData)
    }

    this.service.orderPlace(encryptData)
      .subscribe({
        next: (v: any) => {
          if (v.status) {
            this.toastr.success(v.message)
            this.form.reset()

          } else {
            this.toastr.success(v.message)
          }
         
        },
        error: (err: any) => {
      
          if (err.error.message) {
            this.toastr.error(err.error.message)
          } else if (err.error.errors) {
            const validationErrors = err.error.errors;
            this.serverErrors = validationErrors || {};
          }
        
        }
      })
  }

  startCooldownTimer() {
    this.timerInterval = setInterval(() => {
      this.cooldown--;
      if (this.cooldown <= 0) this.stopCooldownTimer();
    }, 1000);
  }

  stopCooldownTimer() {
    if (this.timerInterval) {
      clearInterval(this.timerInterval);
      this.timerInterval = null;
    }
    this.cooldown = 0;
  localStorage.removeItem(`limitOrderCooldown_${this.buyorsell}`);
  }

restoreCooldown() {
  const saved = localStorage.getItem(`limitOrderCooldown_${this.buyorsell}`);
  if (saved) {
    const remaining = Math.floor((parseInt(saved) - Date.now()) / 1000);
    if (remaining > 0) {
      this.cooldown = remaining;
      this.startCooldownTimer();
    } else {
      localStorage.removeItem(`limitOrderCooldown_${this.buyorsell}`);
    }
  }
}


  

  navigateToLogin() {
    this.router.navigateByUrl('signin')
  }
  checktds(firstcurrency: any, secondcurrency: any) {
    if (secondcurrency === 'INR' && this.buyorsell === 'buy') {
      this.tdsapply = false;
      // console.log("tds will not apply", firstcurrency, "AND", secondcurrency, "buyorsell", this.buyorsell);
    }
    else{
      this.tdsapply = true;
      // console.log("tds will apply", firstcurrency, "AND", secondcurrency, "buyorsell", this.buyorsell);
    }
  }
}
